<template>
  <main class="activity-container">
    <activity-intro v-if="showActivityIntro"></activity-intro>
    <activity-loader v-if="dataLoaded && isMounted && showComponent"></activity-loader>
  </main>
</template>

<script>
import { createNamespacedHelpers, mapActions, mapMutations, mapState, mapGetters } from 'vuex';
import ActivityIntro from 'components/activity/ActivityIntro.vue';
import MetaHelper from 'src/mixins/MetaHelper';

const activity = createNamespacedHelpers('activity');
const application = createNamespacedHelpers('application');

const ActivityLoader = () => import('components/activity/ActivityLoader.vue');
// process.env.KWT_TARGET === 'ios'
//   ? require('components/activity/ActivityLoaderChunkless.vue')
// :

export default {
  name: 'ActivityContainer',

  components: {
    ActivityIntro,
    ActivityLoader
  },
  mixins: [MetaHelper],
  data() {
    return {
      extKeyAttemptEvent: null,
      // Should be props in Vue 2 from router
      isMounted: false,
      showComponent: true
    };
  },

  computed: {
    ...mapState('activity', ['data', 'dataLoaded', 'isRestarting', 'viewedIntro']),

    ...mapState('activity', {
      showIntro: ({ viewedIntro, showIntro }) => !viewedIntro && showIntro,
      isSpotCheck: state => Boolean(state.data.spotCheck)
    }),

    ...mapState('application', ['device', 'isLiteVersion', 'accessibilityMode']),
    ...mapGetters('application', ['needsToChooseKeyboard']),

    ...application.mapState(['kwtMode', 'device']),

    isAssessmentAcceleratorTesterActivity() {
      return !!(this.isAssessmentAcceleratorActivity() && ['tester'].includes(this.kwtMode));
    },

    isAssessmentAcceleratorPreview() {
      return !!(
        this.isAssessmentAcceleratorActivity() && ['preview', 'previewLite'].includes(this.kwtMode)
      );
    },

    showActivityIntro() {
      return (
        (!this.isAssessmentAcceleratorActivity() ||
          (this.isAssessmentAcceleratorActivity() && this.showAssessmentAcceleratorIntro) ||
          this.isAssessmentAcceleratorPreview) &&
        this.showIntro &&
        this.isMounted &&
        this.showComponent &&
        !this.isAssessmentAcceleratorTesterActivity
      );
    }
  },

  mounted() {
    this.isMounted = true;
    this.onMount();
  },

  methods: {
    ...mapActions('activity', ['restartActivity']),
    ...mapMutations('application', ['setToolbarMode']),
    ...mapMutations('activity', ['setActivityRestarting', 'setIntroViewed']),

    handleKeyboardInput(event) {
      this.extKeyAttemptEvent = event;
    },

    handleViewedAssessmentAcceleratorIntro() {
      this.setToolbarMode('fullScreenOnly');

      // We need a time gap between setting the toolbar mode and viewedIntro property
      setTimeout(() => {
        this.setIntroViewed();
      }, 500);
    },

    onMount() {
      if (
        this.needsToChooseKeyboard &&
        this.dataLoaded &&
        ((this.isAssessmentAcceleratorActivity() && !this.isAssessmentAcceleratorTesterActivity) ||
          this.data.keyboard)
      ) {
        const query = { return: this.$router.app._route.path }; // eslint-disable-line
        this.$router.replace({ path: '/keyboard-select', query });
        return;
      }

      if (
        this.isAssessmentAcceleratorActivity() &&
        !this.showActivityIntro &&
        !this.isAssessmentAcceleratorTesterActivity
      ) {
        this.handleViewedAssessmentAcceleratorIntro();
      } else {
        this.setToolbarMode('inActivityPanel');
      }
    }
  },

  watch: {
    isRestarting(newRestarting, oldRestarting) {
      if (newRestarting && !oldRestarting) {
        // restart activity
        this.showComponent = false;
        this.restartActivity(false);
        this.$nextTick(() => this.setActivityRestarting(false, this.isSpotCheck));
      } else {
        // show component again
        this.extKeyAttemptEvent = null;

        this.$nextTick(() => {
          this.showComponent = true;
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-container {
  height: 100%;
}

.is-chrome-ipad {
  .activity-container {
    height: calc(var(--vh, 1vh) * 100);
  }
}

.activity-wrapper {
  height: 100%;
  width: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.hidden-input {
  opacity: 0;
  font-size: 0;
  color: transparent;
  position: absolute;
  top: -200px;
}
</style>
